import React, { useMemo, useState } from 'react';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { faFileLines } from '@fortawesome/pro-light-svg-icons';
import Button from 'components/UI/V3/Button/Button';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import { SelectionBar } from 'components/UI/V3/SelectionBar/SelectionBar';
import EmptyState from 'components/common/EmptyState';
import { ConfirmDialog } from 'components/UI/ConfirmDialog/ConfirmDialog';
import { useEmailMarketing } from '../EmailMarketingProvider';
import { EmailTemplate, TemplateSource } from '../types';
import { templateCategoryLabel } from '../utils';
import TemplateCard from './TemplateCard';
import TemplatePreviewModal from './TemplatePreviewModal';
import TemplateEditor from './TemplateEditor';
import CopyToLocationsModal from './CopyToLocationsModal';
import styles from './TemplatesTab.module.scss';

type CategoryFilter = string | 'all';

const SOURCE_OPTIONS = [
    { id: 'location', name: 'Location templates' },
    { id: 'gallery', name: 'Cuddles gallery' }
];

export const TemplatesTab: React.FC = () => {
    const { loadingTemplates, templates, scopeIds, locationName, openNewCampaign, duplicateTemplate, deleteTemplate } =
        useEmailMarketing();

    const [source, setSource] = useState<TemplateSource>('location');
    const [category, setCategory] = useState<CategoryFilter>('all');
    const [search, setSearch] = useState('');
    const [previewing, setPreviewing] = useState<EmailTemplate | null>(null);
    const [editing, setEditing] = useState<EmailTemplate | 'new' | null>(null);
    const [copying, setCopying] = useState<EmailTemplate | null>(null);
    const [toDelete, setToDelete] = useState<EmailTemplate | null>(null);

    const isGallery = source === 'gallery';

    // Gallery categories come from master data, so the filter lists the ones in use.
    const categoryOptions = useMemo(() => {
        const ids = Array.from(new Set(templates.filter(item => item.source === 'gallery').map(item => item.category)));
        return [
            { id: 'all' as CategoryFilter, name: 'All' },
            ...ids.map(id => ({ id, name: templateCategoryLabel(id) }))
        ];
    }, [templates]);

    const visible = useMemo(() => {
        const term = search.trim().toLowerCase();
        return templates.filter(template => {
            if (template.source !== source) return false;
            // Location templates follow the global location filter.
            if (template.source === 'location' && !scopeIds.includes(template.locationId as number)) return false;
            if (isGallery && category !== 'all' && template.category !== category) return false;
            return !term || template.name.toLowerCase().includes(term);
        });
    }, [templates, source, scopeIds, isGallery, category, search]);

    return (
        <div className={styles.root}>
            <div className={styles.toolbarTop}>
                <SelectionBar
                    options={SOURCE_OPTIONS}
                    selectedId={source}
                    background="#f8f8f8"
                    onSelect={id => setSource(id as TemplateSource)}
                />
                <Button
                    variant="outlined"
                    leftIcon={faPlus}
                    label="New template"
                    className={styles.newButton}
                    onClick={() => setEditing('new')}
                />
            </div>

            <p className={styles.hint}>
                {isGallery
                    ? 'Ready-made designs by Cuddles. Add one to your locations to get an editable copy.'
                    : 'Each template belongs to a location. Copy it to other locations to reuse it.'}
            </p>

            <SearchBar
                searchText={search}
                placeholder="Search templates"
                onChangeSearchText={setSearch}
            />

            {/* Categories only exist in the Cuddles gallery; same control as the Campaigns status filter. */}
            {isGallery && (
                <SelectionBar
                    options={categoryOptions}
                    selectedId={category}
                    background="#f8f8f8"
                    className={styles.categories}
                    onSelect={id => setCategory(id as CategoryFilter)}
                />
            )}

            {loadingTemplates ? (
                <div className={styles.loading}>
                    <Spinner />
                </div>
            ) : visible.length === 0 ? (
                <div className={styles.empty}>
                    <EmptyState
                        icon={faFileLines}
                        title={search ? 'No templates found' : 'No templates for these locations'}
                        description={
                            search
                                ? 'Try another name or category.'
                                : 'Create a template or add one from the Cuddles gallery.'
                        }
                        showButton={!search}
                        buttonLabel="New template"
                        buttonIcon={faPlus}
                        onButtonClick={() => setEditing('new')}
                    />
                </div>
            ) : (
                <div className={styles.grid}>
                    {visible.map(template =>
                        template.source === 'location' ? (
                            <TemplateCard
                                key={template.id}
                                template={template}
                                locationName={locationName(template.locationId)}
                                onPreview={() => setPreviewing(template)}
                                onUse={() => openNewCampaign(template.id)}
                                onEdit={() => setEditing(template)}
                                onCopy={() => setCopying(template)}
                                onDuplicate={() => duplicateTemplate(template, template.locationId as number)}
                                onDelete={() => setToDelete(template)}
                            />
                        ) : (
                            <TemplateCard
                                key={template.id}
                                template={template}
                                onPreview={() => setPreviewing(template)}
                                onUse={() => setCopying(template)}
                            />
                        )
                    )}
                </div>
            )}

            {previewing && (
                <TemplatePreviewModal
                    template={previewing}
                    locationName={locationName(previewing.locationId)}
                    onClose={() => setPreviewing(null)}
                    onUse={() => {
                        setPreviewing(null);
                        openNewCampaign(previewing.id);
                    }}
                />
            )}

            {editing && (
                <TemplateEditor
                    templateId={editing === 'new' ? null : editing.id}
                    defaultLocationId={scopeIds.length === 1 ? scopeIds[0] : null}
                    onClose={() => setEditing(null)}
                />
            )}

            {copying && (
                <CopyToLocationsModal
                    template={copying}
                    onClose={() => setCopying(null)}
                />
            )}

            <ConfirmDialog
                open={toDelete !== null}
                title="Delete template"
                question={`Delete “${toDelete?.name}”?`}
                subquestion={`It will be removed from ${locationName(
                    toDelete?.locationId ?? null
                )}. Campaigns already sent are not affected.`}
                cancelButtonLabel="Cancel"
                confirmButtonLabel="Delete"
                onCancel={() => setToDelete(null)}
                onConfirm={() => {
                    if (toDelete) deleteTemplate(toDelete);
                    setToDelete(null);
                }}
            />
        </div>
    );
};

export default TemplatesTab;
